import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "reduxStore/state";
import * as Yup from "yup";
import {
  updateDivision,
  createDivision,
} from "reduxStore/actions/divisionActions";
import { searchLocations } from "reduxStore/actions/locationActions";
import { useEffect, useState } from "react";
import { debounce } from "lodash";

export const useDivisionForm = ({ editMode, currentDivision, setOpen }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const organisationId = user.organisation;

  const locationSearchResults =
    useSelector((state) => state.locationSearchResults) || [];
  const [searchLocationTerm, setSearchLocationTerm] = useState("");

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Division Name is required"),
    locations: Yup.array()
      .min(1, "At least one location is required")
      .of(
        Yup.object().shape({
          _id: Yup.string().required("Location ID is required"),
          name: Yup.string().required("Location Name is required"),
        })
      ),
  });

  const initialValues = {
    name: currentDivision ? currentDivision.name : "",
    locations: currentDivision ? currentDivision.locations : [],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    values.organisation = organisationId;
    try {
      let response;
      if (editMode) {
        response = await dispatch(updateDivision(currentDivision._id, values));
      } else {
        response = await dispatch(createDivision(values));
      }

      if (response) {
        dispatch(
          setSnackbar({
            open: true,
            message: editMode
              ? "Division updated successfully"
              : "Division created successfully",
            severity: "success",
          })
        );
      } else {
        dispatch(
          setSnackbar({
            open: true,
            message: editMode
              ? "Division update failed"
              : "Division creation failed",
            severity: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message: error.message || "An error occurred",
          severity: "error",
        })
      );
    } finally {
      setSubmitting(false);
      setOpen(false);
    }
  };

  // Debounced input search term for location
  useEffect(() => {
    const debounceFetch = debounce(() => {
      if (searchLocationTerm) {
        dispatch(searchLocations(searchLocationTerm));
      }
    }, 300); // 300ms delay
    debounceFetch();

    // Cleanup function to cancel the debounce on unmount
    return () => {
      debounceFetch.cancel();
    };
  }, [searchLocationTerm, dispatch]);

  return {
    validationSchema,
    initialValues,
    handleSubmit,
    locationSearchResults,
    setSearchLocationTerm,
  };
};
